<script lang="ts">
    import tabIcon from "@/assets/narvarostatistik.svg";

    export default {
        name: "FormAktivitetstyp",
        tabName: "Aktivitetstyp",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import { computed, ref, toRefs } from "vue";
    import emitter from "tiny-emitter/instance";
    import { AxiosError } from "axios";
    import { useStore } from "@/store";
    import { flashMessage, flashWarning } from "@/utils";
    import { useKForm } from "@/composables/useKForm";
    import KForm from "@/components/forms/Form/KForm.vue";
    import KHeader from "@/components/KHeader.vue";
    import KAutocomplete from "@/components/forms/inputs/Autocomplete/KAutocomplete.vue";
    import KText from "@/components/forms/inputs/Text/KText.vue";

    const store = useStore();

    const props = defineProps<{
        tabId: number;
        parameters?: {
            id: string;
            namn: string;
            typ: string;
        };
    }>();

    const { parameters, tabId } = toRefs(props);

    const { inputErrors, formErrors, handleErrorResponse } = useKForm();

    const formRef = ref();

    const isEditing = computed(() => !!parameters?.value?.id);

    const heading = computed(() => (isEditing.value ? "Ändra på aktivitetstyp" : "Skapa ny aktivitetstyp"));

    const submitLabel = computed(() => (isEditing.value ? "Uppdatera aktivitetstyp" : "Skapa aktivitetstyp"));

    const hasUnsavedData = computed(() => formRef.value?.hasUnsavedData?.());

    defineExpose({ hasUnsavedData, tabId });

    const typOptions = [
        { value: "Träning", label: "Träning" },
        { value: "Match", label: "Match" },
    ];

    const initForm = () => ({ ...parameters?.value });

    const onSubmit = async (formData: any) => {
        const aktivitetstyp = isEditing.value ? { ...formData, id: parameters?.value?.id } : formData;
        try {
            if (isEditing.value) {
                await store.dispatch("skreg/aktivitetstyper/updateAktivitetstyp", { aktivitetstyp });
            } else {
                await store.dispatch("skreg/aktivitetstyper/createAktivitetstyp", { aktivitetstyp });
            }
            flashMessage(isEditing.value ? "Aktivitetstypen har uppdaterats" : "Aktivitetstypen har skapats", 3000);
            store.dispatch("tabs/closeTab", tabId.value);
        } catch (thrownError) {
            const error = thrownError as AxiosError;
            if (error?.response?.data) {
                handleErrorResponse(error);
                return;
            }
            flashWarning(
                isEditing.value ? "Kunde inte uppdatera aktivitetstypen." : "Kunde inte spara aktivitetstypen."
            );
        }
    };

    const onCancel = ({ hasUnsavedData }: { hasUnsavedData: boolean }) => {
        if (hasUnsavedData) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    { title: "Fortsätt redigera", type: "secondary" },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => store.dispatch("tabs/closeTab", tabId.value),
                    },
                ],
            });
            return;
        }
        store.dispatch("tabs/closeTab", tabId.value);
    };
</script>

<template>
    <div class="aktivitetstyp-form">
        <KForm
            ref="formRef"
            id="aktivitetstyp-form"
            :initial-values-getter="initForm"
            :submit-content="submitLabel"
            cancel-content="Avbryt"
            :disableSubmit="!hasUnsavedData"
            :errors="inputErrors"
            :formErrors="formErrors"
            @submit="onSubmit"
            @cancel="onCancel"
        >
            <template #header>
                <KHeader :heading="heading" />
            </template>
            <template #default>
                <div class="form-content">
                    <KText name="namn" label="Namn" validation="required" />
                    <KAutocomplete name="typ" label="Typ" validation="required" :options="typOptions" />
                </div>
            </template>
        </KForm>
    </div>
</template>

<style lang="scss" scoped></style>
